import React, { useState } from 'react';

import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';
import { nanoid } from '@reduxjs/toolkit';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { bemCssModules } from '@utils';
import { useNavigate, useNotifications } from '@utils/hooks';

import { useStore, withdrawActions } from '@modules';
import { ProtectNextEnum } from '@modules/types';
import { GETWithdrawRateResponse } from '@modules/withdraw/types';

import Verification from '@containers/Verification';

import style from './Verification.scss';

const cn = bemCssModules(style, 'Verification');
export type WithdrawVerifyEmailType = {
  rate?: GETWithdrawRateResponse;
  onSuccess(): void;
};

const { verifyOTP } = ProtectNextEnum;

export const WithdrawVerifyEmail: CustomFC<WithdrawVerifyEmailType> = props => {
  const { rate, onSuccess } = props;
  const { t } = useTranslation();
  const { withdrawSuccessNotification, withdrawErrorNotification } = useNotifications();
  const [resendButtonId] = useState(nanoid());
  const navigate = useNavigate();
  const { state } = useLocation();

  const [{ withdraw, user, global }, dispatch] = useStore();
  const isLoading = !!withdraw.loaders.withdrawVerifyEmail;
  const error = withdraw.errors?.data?.code?.[0] || '';
  const email = user.data?.email;

  const handleSubmit = (code: string, key: string) => {
    dispatch(withdrawActions.withdrawVerifyEmail({ key, code }))
      .unwrap()
      .then(res => {
        if (!rate) return;

        if (res.data.next === verifyOTP) {
          navigate<'verifications'>(CONST.nav.verification, {
            state: {
              ...state,
              rate,
              flow: 'WithdrawVerify2FA',
              token: res.data.token,
            },
          });
        } else {
          const displayOptions = global.currencies.config.display_options;
          const displayDigits = displayOptions[rate.currency]?.display_digits;
          const totalDigits = displayOptions[rate.currency]?.total_digits;

          const message = `${t('notifications.success_withdraw')} ${correctingTrailingNumbers(
            rate.total[rate.currency] || '',
            displayDigits,
            totalDigits
          )} ${rate.currency}`;
          withdrawSuccessNotification(message);

          onSuccess();
        }
      })
      .catch(err => withdrawErrorNotification(err.message));
  };

  const tooltip = (
    <>
      <b>{t('verify.email.tooltip.title')}</b>
      <div className={cn('list-wrapper')}>
        <ol className={cn('tooltip-list')}>
          <li className={cn('item')}>
            <Trans i18nKey="verify.email.tooltip.li1">
              text
              <label htmlFor={resendButtonId} className={cn('link', ['link'])} />.
            </Trans>
          </li>
          <li className={cn('item')}>
            <Trans i18nKey="verify.email.tooltip.li2">
              text
              <Link className="link" to={CONST.nav.profile}>
                text
              </Link>
              .
            </Trans>
          </li>
          <li className={cn('item')}>
            <Trans i18nKey="verify.email.tooltip.li3">
              text
              <a className="link" href="mailto:support@mercuryo.io">
                text
              </a>
            </Trans>
          </li>
        </ol>
      </div>
    </>
  );

  return (
    <Verification
      isWithdraw
      description={t('withdraw.verify_description', { field: email })}
      customInputClass={cn('custom-input')}
      onVerify={handleSubmit}
      isLoading={isLoading}
      type="email"
      error={error}
      tooltip={tooltip}
      id={resendButtonId}
    />
  );
};

export default WithdrawVerifyEmail;
